import { EXPENSE_TYPE_TREE, MainCategory } from 'src/Constants/expenseCategorization';
import {
  BudgetForecastLineRequestDtoCategoryEnum,
  BudgetForecastLineRequestDtoMainCategoryEnum,
  BudgetForecastLineRequestDtoSubCategoryEnum,
  CreateExpenseDtoTypeEnum,
} from 'src/Services/API/generated';

/**
 * The budget line enums and the expense enums are generated separately from the same API
 * source, so their members are identical. The two casts below are the single place where the
 * front crosses from one generated name to the other.
 */
export type BudgetCategory = BudgetForecastLineRequestDtoCategoryEnum;
export type BudgetMainCategory = BudgetForecastLineRequestDtoMainCategoryEnum;
export type BudgetSubCategory = BudgetForecastLineRequestDtoSubCategoryEnum;

export interface BudgetCategoryPlacement {
  mainCategory: BudgetMainCategory;
  subCategory: BudgetSubCategory;
}

/** Where a reconciliation category sits in the budget tree. */
export const placementOfCategory = (category: BudgetCategory): BudgetCategoryPlacement => {
  const meta = EXPENSE_TYPE_TREE[category as unknown as CreateExpenseDtoTypeEnum];
  return {
    mainCategory: meta.main as unknown as BudgetMainCategory,
    subCategory: meta.sub as unknown as BudgetSubCategory,
  };
};

const categoriesOfMainCategories = (mainCategories: MainCategory[]): BudgetCategory[] =>
  (Object.keys(EXPENSE_TYPE_TREE) as CreateExpenseDtoTypeEnum[])
    .filter(type => mainCategories.includes(EXPENSE_TYPE_TREE[type].main))
    .map(type => type as unknown as BudgetCategory);

/** Categories offered by « Ajouter un frais fixe » (step 1). */
export const FIXED_COST_CATEGORIES = categoriesOfMainCategories([MainCategory.FixedCosts]);

/** Categories offered by « Ajouter des travaux non listés » (step 3). */
export const WORKS_CATEGORIES = categoriesOfMainCategories([MainCategory.WorksEquipment]);

/** Categories offered by « Ajouter une recette » (step 4). */
export const INCOME_CATEGORIES = categoriesOfMainCategories([
  MainCategory.FundingCalls,
  MainCategory.RentalHousing,
  MainCategory.EventsTourism,
  MainCategory.EstateLands,
  MainCategory.Operating,
  MainCategory.Subsidies,
  MainCategory.Reimbursements,
]);

export const FUND_CALL_MAIN_CATEGORY = MainCategory.FundingCalls as unknown as BudgetMainCategory;

export const DAILY_LIFE_MAIN_CATEGORY = MainCategory.DailyLife as unknown as BudgetMainCategory;

export const WORKS_MAIN_CATEGORY = MainCategory.WorksEquipment as unknown as BudgetMainCategory;

/**
 * Reconciliation category of a fund call, chosen from its cadence: a monthly call is matched
 * against the real MONTHLY_TRANSFER operations, a quarterly one against QUARTERLY_TRANSFER…
 * An uncommon cadence falls back to the catch-all fund call type.
 */
export const fundCallCategoryOfFrequency = (frequencyPerYear: number): BudgetCategory => {
  if (frequencyPerYear === 12) return 'MONTHLY_TRANSFER' as BudgetCategory;
  if (frequencyPerYear === 4) return 'QUARTERLY_TRANSFER' as BudgetCategory;
  if (frequencyPerYear === 2) return 'SEMI_ANNUAL_TRANSFER' as BudgetCategory;
  if (frequencyPerYear === 1) return 'ANNUAL_TRANSFER' as BudgetCategory;
  return 'OTHER_FUND_CALL' as BudgetCategory;
};

/** The four cadences the wizard offers, in the order of the brief. */
export const FUND_CALL_FREQUENCIES = [12, 4, 1, 0.5];
